// Example3SizeLimitation.js - Size Limitation Check Example
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { normalizeModel } from "../utils/normalizeModel";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./Example3SizeLimitation.css";

const Example3SizeLimitation = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const machineVisualizationRef = useRef([]);

  const { createVisualization } = useVisualHelpers();
  const [currentStep, setCurrentStep] = useState(1);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [driverObj, setDriverObj] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [machineConstraints, setMachineConstraints] = useState({
    printer3D: { width: 200, height: 200, depth: 150 }, // Small printer
    laserCutter: { width: 300, height: 300, maxThickness: 5 },
    wireBender: { maxLength: 1000, maxWireGauge: 2.5 },
    hotwireCutter: { width: 400, height: 400, maxZ: 250 }
  });

  const pageId = useMemo(() => `size-limitation-example`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // 工作流程选项
  const workflowOptions = [
    {
      id: 'FoundationalWorkflows_GCode Generator',
      name: '3D Printing',
      machine: 'printer3D',
      icon: '🖨️',
      description: 'Layer-by-layer FDM printing'
    },
    {
      id: 'FoundationalWorkflows_Laser cut stacked sheets (Algorithm 1)',
      name: 'Laser Cut Stacked',
      machine: 'laserCutter', 
      icon: '📚',
      description: 'Horizontal slices, stacked assembly'
    },

    {
      id: 'FoundationalWorkflows_Wire mesh sculpture (Algorithm 1)',
      name: 'Wire Mesh',
      machine: 'wireBender',
      icon: '🕸️',
      description: 'Structural wire framework'
    },
    {
      id: 'FoundationalWorkflows_Hot Wire Foam Cutting',
      name: 'Hot Wire Cutting',
      machine: 'hotwireCutter',
      icon: '🔥',
      description: 'Foam cutting with hot wire'
    }
  ];

  // 初始化driver.js
  useEffect(() => {
    const newDriverObj = driver({
      popoverClass: "cameleon-tutorial-popover",
      stagePadding: 4,
    });
    setDriverObj(newDriverObj);

    return () => {
      if (newDriverObj) {
        newDriverObj.destroy();
      }
    };
  }, []);

  // 检查尺寸兼容性
  const checkSizeCompatibility = useCallback((workflow, dimensions) => {
    const constraints = machineConstraints[workflow.machine];
    if (!constraints || !dimensions) return { compatible: false, reason: 'Unknown' };

    switch (workflow.machine) {
      case 'printer3D':
        if (dimensions.width > constraints.width || 
            dimensions.height > constraints.height || 
            dimensions.depth > constraints.depth) {
          return {
            compatible: false,
            reason: `Exceeds print bed size (${constraints.width}×${constraints.height}×${constraints.depth}mm)`
          };
        }
        break;
      
      case 'laserCutter':
        // Laser cutting is generally more flexible for large objects
        return {
          compatible: true,
          reason: 'Can cut large sheets and assemble'
        };
      
      case 'wireBender':
        // Wire mesh can handle large structures
        return {
          compatible: true,
          reason: 'Structural framework scales well'
        };
      
      case 'hotwireCutter':
        if (dimensions.depth > constraints.maxZ) {
          return {
            compatible: false,
            reason: `Exceeds cutting height (max ${constraints.maxZ}mm)`
          };
        }
        break;
    }

    return { compatible: true, reason: 'Within machine limits' };
  }, [machineConstraints]);

  // 创建机器约束可视化
  const createMachineVisualization = useCallback(() => {
    if (!sceneRef.current) return;

    // 清除旧的可视化
    machineVisualizationRef.current.forEach(obj => {
      sceneRef.current.remove(obj);
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) obj.material.dispose();
    });
    machineVisualizationRef.current = [];

    // 创建3D打印机床尺寸可视化
    const printerConstraints = machineConstraints.printer3D;
    const printerGeometry = new THREE.BoxGeometry(
      printerConstraints.width,
      printerConstraints.height, 
      printerConstraints.depth
    );
    const printerMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.2,
      wireframe: true
    });
    const printerBox = new THREE.Mesh(printerGeometry, printerMaterial);
    printerBox.position.set(-150, printerConstraints.height/2, 0);
    
    // 添加标签
    const printerLabel = createTextSprite('3D Printer Limit\n200×200×150mm');
    printerLabel.position.set(-150, printerConstraints.height + 30, 0);
    
   //sceneRef.current.add(printerBox);
   //sceneRef.current.add(printerLabel);
    machineVisualizationRef.current.push(printerBox, printerLabel);

  }, [machineConstraints]);

  // 创建文本精灵
  const createTextSprite = useCallback((message) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const fontSize = 16;
    
    canvas.width = 256;
    canvas.height = 128;
    
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = 'rgba(255, 255, 255, 0.9)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'rgba(0, 0, 0, 1.0)';
    
    const lines = message.split('\n');
    lines.forEach((line, index) => {
      context.fillText(line, 10, fontSize + (index * (fontSize + 4)));
    });
    
    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({ map: texture });
    const sprite = new THREE.Sprite(spriteMaterial);
    sprite.scale.set(50, 25, 1);
    
    return sprite;
  }, []);

  // 高亮sidebar中的Lab Equipment区域
  const highlightLabEquipment = useCallback(() => {
    if (driverObj) {
      driverObj.highlight({
        element: "#lab-equipment-section",
        popover: {
          side: "right",
          title: "🔧 Lab Equipment & Materials",
          description: "These are your available machines and their size constraints. Notice how the 3D printer bed (200×200×150mm) is much smaller than your target sculpture size!",
          nextBtnText: "Got it!",
        }
      });
    }
  }, [driverObj]);

  // 高亮推荐的工作流程
  const highlightRecommendedWorkflow = useCallback((workflowId, title, advantages) => {
    if (driverObj) {
      driverObj.highlight({
        element: `[data-workflow="${workflowId}"]`,
        popover: {
          side: "right",
          title: title,
          description: `✅ This workflow is compatible with large geometries!\n\nKey advantages: ${advantages.join(', ')}`,
          nextBtnText: "Got it!",
        }
      });

      // 高亮关键词
      advantages.forEach(keyword => {
        const chipElement = document.querySelector(`[data-keyword="${keyword}"]`);
        if (chipElement) {
          chipElement.classList.add('highlight-pulse');
          setTimeout(() => {
            chipElement.classList.remove('highlight-pulse');
          }, 4000);
        }
      });
    }
  }, [driverObj]);

  // 显示解决方案
  const showSolution = useCallback((solutionType) => {
    const solutions = {
      'laser-stacked': {
        workflowId: 'FoundationalWorkflows_Laser cut stacked sheets (Algorithm 1)',
        title: '📚 Laser Cut Stacked Solution',
        advantages: ['Easy to Assemble', 'Wood', 'Modularity'],
        explanation: 'Break large geometry into stackable layers. Each layer fits within material constraints.'
      },

      'wire-mesh': {
        workflowId: 'FoundationalWorkflows_Wire mesh sculpture (Algorithm 1)',
        title: '🕸️ Wire Mesh Framework',
        advantages: ['Wire', 'Lightweight', 'Flexibility'],
        explanation: 'Create structural skeleton. Size constraints become irrelevant with wire framework.'
      }
    };

    const solution = solutions[solutionType];
    if (solution) {
      highlightRecommendedWorkflow(solution.workflowId, solution.title, solution.advantages);
    }
  }, [highlightRecommendedWorkflow]);

  // 加载模型
  const loadModel = useCallback(() => {
    setIsLoading(true);
    const loader = new STLLoader();
    const modelPath = "/models/twistT.stl"; // 使用现有模型，但我们会放大它

    loader.load(
      modelPath,
      (geometry) => {
        const material = new THREE.MeshNormalMaterial({ transparent: true, opacity: 0.8 });
        const mesh = new THREE.Mesh(geometry, material);
        
        // 标准化模型
        normalizeModel(mesh);
        
        // 放大模型来模拟400×300×200mm的大雕塑
        const scaleFactor = 3; // 放大3倍
        mesh.scale.setScalar(scaleFactor);
        mesh.updateMatrixWorld(true);
        
        // 计算实际尺寸
        const bbox = new THREE.Box3().setFromObject(mesh);
        const size = bbox.getSize(new THREE.Vector3());
        
        setModelDimensions({
          width: Math.round(size.x),
          height: Math.round(size.y), 
          depth: Math.round(size.z)
        });

        modelRef.current = mesh;
        sceneRef.current.add(mesh);
        
        // 创建可视化辅助
        createVisualization(mesh, sceneRef.current, bbox);
        
        // 创建机器约束可视化
        createMachineVisualization();
        
        setIsLoading(false);
      },
      undefined,
      (err) => {
        setError("Failed to load model");
        setIsLoading(false);
      }
    );
  }, [createVisualization, createMachineVisualization]);

  // 初始化场景
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    setTimeout(() => {
      loadModel();
    }, 0);

    return cleanup;
  }, [loadModel]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>

      {/* Step 1 - 场景介绍 */}
      {!isLoading && currentStep === 1 && modelDimensions && (
        <div className="instruction-box">
          <h3>📏 Step 1: Size Challenge</h3>
          <p>
            <strong>Scenario:</strong> Peter wants to fabricate this sculpture:
          </p>
          <div className="size-display">
            <div className="size-info">
              <strong>Target Size:</strong> {modelDimensions.width}×{modelDimensions.height}×{modelDimensions.depth}mm
            </div>
            <div className="constraint-info">
              <strong>Lab Equipment Constraints:</strong>
              <p style={{ fontSize: '14px', margin: '8px 0', fontStyle: 'italic' }}>
                📍 Check the <strong>"Lab Equipment & Materials Available"</strong> section in the sidebar →
              </p>
              <ul>
                <li>🖨️ <strong>3D Printer:</strong> 200×200×150mm bed (❌ too small!)</li>
                <li>📚 <strong>Laser Cutter:</strong> 300×300mm sheets (✅ can cut panels)</li>
                <li>🕸️ <strong>Wire Bender:</strong> 1000mm max length (✅ scalable)</li>
                <li>🔥 <strong>Hot Wire:</strong> 400×400mm, 250mm height (❌ height limit!)</li>
              </ul>
            </div>
          </div>
          <p>The red wireframe shows the 3D printer's size limit for comparison.</p>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', margin: '15px 0' }}>
            <button className="action-button" onClick={highlightLabEquipment}>
              🎯 Highlight Equipment Constraints
            </button>
            <button className="action-button" onClick={() => setCurrentStep(2)}>
              Show Solutions →
            </button>
          </div>
        </div>
      )}

      {/* Step 2 - 推荐解决方案（原Step 4) */}
      {!isLoading && currentStep === 2 && (
        <div className="instruction-box">
          <h3>💡 Step 2: Size-Compatible Solutions</h3>
          
          <div className="analysis-summary">
            <div className="feasible-section">
              <h4>✅ Feasible Workflows:</h4>
              <p style={{ fontSize: '14px', color: '#666', margin: '5px 0 15px 0' }}>
                These workflows can handle large geometries by breaking them into manageable pieces:
              </p>
            </div>
            
            <div className="problematic-section">
              <h4>❌ Size-Limited Workflows:</h4>
              <ul style={{ fontSize: '14px', color: '#666', margin: '5px 0 15px 0' }}>
                <li><strong>3D Printing:</strong> Exceeds 200×200×150mm bed</li>
                <li><strong>Hot Wire:</strong> Exceeds 250mm height limit</li>
              </ul>
            </div>
          </div>
          
          <div style={{ margin: '20px 0' }}>
            <button 
              className="action-button solution-button"
              style={{ 
                width: '100%', 
                margin: '8px 0',
                padding: '15px',
                border: '2px solid #3288e6',
                background: '#f0f7ff'
              }}
              onClick={() => showSolution('laser-stacked')}
            >
              📚 Try Laser Cut Stacked Layers
              <div className="button-subtitle">
                ✅ Break into stackable sheets → scalable assembly
              </div>
            </button>
            
            <button 
              className="action-button solution-button"
              style={{ 
                width: '100%', 
                margin: '8px 0',
                padding: '15px',
                border: '2px solid #4caf50',
                background: '#e8f5e8'
              }}
              onClick={() => showSolution('finger-joints')}
            >
              🔗 Try Finger Joint Assembly
              <div className="button-subtitle">
                ✅ Divide into interlocking panels → strong structure
              </div>
            </button>

            <button 
              className="action-button solution-button"
              style={{ 
                width: '100%', 
                margin: '8px 0',
                padding: '15px',
                border: '2px solid #ff9800',
                background: '#fff3e0'
              }}
              onClick={() => showSolution('wire-mesh')}
            >
              🕸️ Try Wire Mesh Framework
              <div className="button-subtitle">
                ✅ Structural skeleton → no size limits
              </div>
            </button>
          </div>

          <p style={{ fontSize: '14px', fontStyle: 'italic', color: '#666', textAlign: 'center' }}>
            💡 Click each option to see it highlighted in the sidebar workflows!
          </p>

          <button className="action-button" onClick={() => setCurrentStep(3)}>
            I understand the solutions
          </button>
        </div>
      )}

      {/* Step 3 - 完成（原Step 5） */}
      {!isLoading && currentStep === 3 && (
        <div className="instruction-box">
          <h3>🎉 Excellent Work!</h3>
          <p>
            <strong>You've successfully completed Size Limitation Check!</strong>
          </p>
          <div className="completion-summary">
            <h4>✅ What you learned:</h4>
            <ul>
              <li>How to <strong>match geometry size</strong> with machine capabilities</li>
              <li>Understanding <strong>fabrication constraints</strong> and workarounds</li>
              <li>When to choose <strong>assembly-based methods</strong> for large objects</li>
              <li>The importance of <strong>workflow selection</strong> based on scale</li>
            </ul>
          </div>
          
          <p style={{ color: "#007bff", fontWeight: "bold" }}>
            🎯 You're now ready to handle size constraints in real fabrication projects!
          </p>
        </div>
      )}

      {/* 状态信息 */}
      {isLoading && <div className="loading-box">Loading large model...</div>}
      {error && <div className="error-box">Error: {error}</div>}
    </div>
  );
};

export default Example3SizeLimitation;